import type { ZodSchema, ZodTypeDef } from 'zod';
import type { AppConfig } from '../config';

export interface SearchParams {
  q?: string | undefined;
  search_type?: 'contains' | 'starts_with' | undefined;
  hotel?: string | undefined;
  address?: string | undefined;
  category_id?: number | undefined;
  city_id?: number | undefined;
  zip?: string | undefined;
  grade?: string | undefined;
  page?: number | undefined;
  per_page?: number | undefined;
  sort?: string | undefined;
  dir?: 'asc' | 'desc' | undefined;
  near_lat?: number | undefined;
  near_lng?: number | undefined;
  near_radius_miles?: number | undefined;
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly body: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildUrl(base: string, path: string, params?: Record<string, string | number | undefined>): string {
  const url = new URL(`${base.replace(/\/$/, '')}${path}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === '' || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<O, D extends ZodTypeDef, I>(
  config: AppConfig,
  path: string,
  schema: ZodSchema<O, D, I>,
  params?: Record<string, string | number | undefined>,
): Promise<O> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (config.nonce) headers['X-WP-Nonce'] = config.nonce;

  const res = await fetch(buildUrl(config.apiBase, path, params), { headers, credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(`Invalid JSON from ${path}`, res.status, text);
  }
  if (!res.ok) {
    throw new ApiError(`${res.status} ${res.statusText}`, res.status, body);
  }
  return schema.parse(body);
}

import { Lookups, RestaurantDetail, SearchResponse } from './schemas';

export const api = {
  search: (config: AppConfig, params: SearchParams) =>
    request(config, '/restaurants', SearchResponse, params as Record<string, string | number | undefined>),
  detail: (config: AppConfig, permitNumber: string) =>
    request(config, `/restaurants/${encodeURIComponent(permitNumber)}`, RestaurantDetail),
  lookups: (config: AppConfig) => request(config, '/restaurants/lookups', Lookups),
};
